"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DmsDoubleBlind = void 0;
var _react = _interopRequireWildcard(require("react"));
var _reactBootstrap = require("react-bootstrap");
var _material = require("@mui/material");
var _reactIntlUniversal = _interopRequireDefault(require("react-intl-universal"));
var _dmsbasereact = require("dmsbasereact");
var _Email = _interopRequireDefault(require("@mui/icons-material/Email"));
var _Lock = _interopRequireDefault(require("@mui/icons-material/Lock"));
var _Keyboard = _interopRequireDefault(require("@mui/icons-material/Keyboard"));
var _DmsFetch = require("../DmsFetch/DmsFetch.js");
var _server = require("../../service/server/server.js");
var _Fingerprint = _interopRequireDefault(require("@mui/icons-material/Fingerprint"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
/**
 * @Author: Fernando Schneider
 * @Date:   2024-02-21 16:54:24
 */

/**
 * Description: The DoubleBlind component handles user authentication
 * and executes the onSuccess function for users who have the CAN_DOUBLE_BLIND
 * permission and are successfully authenticated.
 *
 * @param {boolean} show - Used to display the modal.
 * @param {function} setShowDoubleBlind - Function to set the show state of the modal.
 * @param {function} onSuccess - Optional function called upon successful authentication and permission check.
 * @param {any} data_ - Optional additional data that may be manipulated within onSuccess.
 * @param {function} onClose - Optional function called when the DoubleBlind modal is closed.
 * @param {string} logModule - Optional log module to indicate from which module the double blind is called.
 * @param {string} headerText - Optional header text to be putted in the modal title.
 * @param {vector} additional_permissions - Optional permission to validate double blind
 * @param {boolean} suppressSuccessToast - Optional flag to suppress the success toast notification after authentication
 */
const DmsDoubleBlind = _ref => {
  let {
    show,
    setShowDoubleBlind,
    onSuccess = null,
    data_ = null,
    onClose = null,
    logModule = "",
    headerText = null,
    additional_permissions = [],
    suppressSuccessToast = false
  } = _ref;
  const [login, setLogin] = (0, _react.useState)();
  const [password, setPassword] = (0, _react.useState)();
  const doubleBlindGenericDescription = _reactIntlUniversal.default.get("doubleBlind").d("double blind");
  const [logDescription, setLogDescription] = (0, _react.useState)("");
  const [error, setError] = (0, _react.useState)(null);
  const [fingerPrinterInfo, setFingerPrinterInfo] = (0, _react.useState)(null);
  const [progressFingerPrinter, setProgressFingerPrinter] = (0, _react.useState)(100);
  const [fingerPrinterEnabled, setFingerPrinterEnabled] = (0, _react.useState)(() => sessionStorage.getItem("fingerPrinterEnabled") === "true" || false);
  const [doubleBlindSuccess, setDoubleBlindSuccess] = (0, _react.useState)(false);
  const [listPermissions, setListPermissions] = (0, _react.useState)(() => {
    const permissionsArray = Array.isArray(additional_permissions) ? additional_permissions : [additional_permissions];
    return [...permissionsArray, _dmsbasereact.Permissions.CAN_DOUBLE_BLIND];
  });
  const doubleBlindHeaderText = headerText ? headerText : _reactIntlUniversal.default.get("DoubleBlind").d("Double Blind");
  (0, _react.useEffect)(() => {
    if (doubleBlindSuccess) {
      setShowDoubleBlind(false);
      if (!suppressSuccessToast) {
        _dmsbasereact.Notifications.toast(_reactIntlUniversal.default.get("Success").d("Success"), _dmsbasereact.Notifications.TYPE_SUCCESS);
      }
      if (onSuccess) {
        onSuccess(data_);
      }
    }
  }, [doubleBlindSuccess, suppressSuccessToast]);
  (0, _react.useEffect)(() => {
    if (fingerPrinterInfo && progressFingerPrinter > 0) {
      const timer = setInterval(() => {
        setProgressFingerPrinter(prev => {
          if (prev > 1) {
            return prev - 1;
          } else {
            clearInterval(timer);
            setFingerPrinterInfo(false);
            return 0;
          }
        });
      }, 100);
      return () => clearInterval(timer);
    }
  }, [fingerPrinterInfo, progressFingerPrinter]);
  const buildLogMessage = (username, description, success) => {
    const userLabel = _reactIntlUniversal.default.get("DoubleBlind.UserLabel").d("User");
    const descLabel = _reactIntlUniversal.default.get("DoubleBlind.DescriptionLabel").d("description");
    const successMsg = _reactIntlUniversal.default.get("DoubleBlind.SuccessLog").d("performed double blind successfully");
    const failMsg = _reactIntlUniversal.default.get("DoubleBlind.FailLog").d("failed double blind");
    const action = success ? successMsg : failMsg;
    return "".concat(userLabel, " ").concat(username, " ").concat(action, "; ").concat(descLabel, " = ").concat(description);
  };
  const handleValidateLogin = async (user_auth, descriptionToLog, authLogin) => {
    var _user_auth$data;
    const scopes = Array.isArray(user_auth === null || user_auth === void 0 || (_user_auth$data = user_auth.data) === null || _user_auth$data === void 0 ? void 0 : _user_auth$data.scopes) ? user_auth.data.scopes : [];
    const userHasAllPermissions = Array.from(listPermissions).every(perm => scopes.includes(perm));
    const usernameForLog = authLogin || login || _reactIntlUniversal.default.get("DoubleBlind.UnknownUser").d("unknown");
    if (user_auth.status === _dmsbasereact.HTTP_STATUS.OK_200 && userHasAllPermissions) {
      const logMessage = buildLogMessage(usernameForLog, descriptionToLog, true);
      (0, _dmsbasereact.log)(logMessage, logModule, _dmsbasereact.LogLevel.INFO, _dmsbasereact.LogType.USER, null, (0, _dmsbasereact.getToken)(), login);
      setDoubleBlindSuccess(true);
    } else if (user_auth.status === _dmsbasereact.HTTP_STATUS.OK_200 && !userHasAllPermissions) {
      const logMessage = buildLogMessage(usernameForLog, descriptionToLog, false);
      (0, _dmsbasereact.log)(logMessage + " - ".concat(_reactIntlUniversal.default.get("USerWithoutPermission").d("User Without Permission")), logModule, _dmsbasereact.LogLevel.WARNING, _dmsbasereact.LogType.USER, null, (0, _dmsbasereact.getToken)(), login);
      setError(_reactIntlUniversal.default.get("USerWithoutPermission").d("User Without Permission"));
    } else if (user_auth.status === _dmsbasereact.HTTP_STATUS.UNAUTHORIZED_401) {
      const logMessage = buildLogMessage(usernameForLog, descriptionToLog, false);
      (0, _dmsbasereact.log)(logMessage + " - ".concat(user_auth.data.detail), logModule, _dmsbasereact.LogLevel.WARNING, _dmsbasereact.LogType.USER, null, (0, _dmsbasereact.getToken)(), login);
      setError(user_auth.data.detail);
    } else if (user_auth.status === _dmsbasereact.HTTP_STATUS.UNPROCESSABLE_ENTITY_422) {
      const details = user_auth.data.detail;
      const errorMessage = details.map(err => "".concat(err.loc, ":").concat(err.message)).join("\n");
      const logMessage = buildLogMessage(usernameForLog, descriptionToLog, false);
      (0, _dmsbasereact.log)(logMessage + " - ".concat(errorMessage), logModule, _dmsbasereact.LogLevel.WARNING, _dmsbasereact.LogType.USER, null, (0, _dmsbasereact.getToken)(), login);
      setError(errorMessage);
    } else {
      var _user_auth$data2;
      const logMessage = buildLogMessage(usernameForLog, descriptionToLog, false);
      (0, _dmsbasereact.log)(logMessage + " - ".concat(((_user_auth$data2 = user_auth.data) === null || _user_auth$data2 === void 0 ? void 0 : _user_auth$data2.detail) || _reactIntlUniversal.default.get("DoubleBlind.UnknownError").d("Unknown error")), logModule, _dmsbasereact.LogLevel.WARNING, _dmsbasereact.LogType.USER, null, (0, _dmsbasereact.getToken)(), login);
      setError(user_auth.data.detail);
    }
  };
  const handleSubmit = async () => {
    setError(null);
    const descriptionToLog = (logDescription === null || logDescription === void 0 ? void 0 : logDescription.trim()) || doubleBlindGenericDescription;
    const authentication_url = "/token";
    const remember = false;
    const user_auth = await (0, _dmsbasereact.loginUser)({
      login,
      password,
      authentication_url,
      remember
    });
    handleValidateLogin(user_auth, descriptionToLog, login);
  };
  const handleSubmitFingerPrinter = async () => {
    setFingerPrinterInfo(true);
    const descriptionToLog = (logDescription === null || logDescription === void 0 ? void 0 : logDescription.trim()) || doubleBlindGenericDescription;
    const user_id = await (0, _DmsFetch.fetchData)((0, _server.getURL)("/finger_printer/device/get_user_template"), "GET", false);
    if (user_id.status === _dmsbasereact.HTTP_STATUS.OK_200) {
      var _user_id$data;
      const user_auth = await (0, _DmsFetch.fetchData)((0, _server.getURL)("/finger_printer/token"), "POST", false, user_id.data.data);
      const fingerPrintUser = ((_user_id$data = user_id.data) === null || _user_id$data === void 0 || (_user_id$data = _user_id$data.data) === null || _user_id$data === void 0 ? void 0 : _user_id$data.user_login) || _reactIntlUniversal.default.get("DoubleBlind.Fingerprint").d("fingerprint");
      handleValidateLogin(user_auth, descriptionToLog, fingerPrintUser);
      setFingerPrinterInfo(false);
      setProgressFingerPrinter(100);
    } else {
      setFingerPrinterInfo(false);
      setError(user_id.data.detail);
      setProgressFingerPrinter(100);
    }
  };
  const handleClose = () => {
    setError(null);
    if (onClose) {
      onClose();
    }
  };
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_dmsbasereact.DmsModal, {
    isVisible: show,
    onClose: handleClose
  }, /*#__PURE__*/_react.default.createElement(_dmsbasereact.DmsModal.Header, {
    title: doubleBlindHeaderText
  }), /*#__PURE__*/_react.default.createElement(_dmsbasereact.DmsModal.Body, null, error && /*#__PURE__*/_react.default.createElement(_material.Box, {
    mb: 2
  }, /*#__PURE__*/_react.default.createElement(_reactBootstrap.Alert, {
    severity: "danger",
    style: {
      whiteSpace: "pre-line"
    }
  }, error, " ")), fingerPrinterInfo && /*#__PURE__*/_react.default.createElement(_material.Box, {
    mb: 2
  }, /*#__PURE__*/_react.default.createElement(_reactBootstrap.Alert, {
    severity: "info",
    style: {
      whiteSpace: "pre-line"
    }
  }, /*#__PURE__*/_react.default.createElement("div", null, _reactIntlUniversal.default.get("PutYourFingerInDevice").d("Put your finger in device")), /*#__PURE__*/_react.default.createElement(_material.LinearProgress, {
    variant: "determinate",
    value: progressFingerPrinter,
    sx: {
      height: "5px",
      marginTop: "10px"
    }
  }))), /*#__PURE__*/_react.default.createElement(_reactBootstrap.Form, {
    onSubmit: handleSubmit
  }, /*#__PURE__*/_react.default.createElement(_material.Box, {
    mb: 2,
    mt: 1
  }, /*#__PURE__*/_react.default.createElement(_material.TextField, {
    fullWidth: true,
    label: _reactIntlUniversal.default.get("User").d("User"),
    placeholder: _reactIntlUniversal.default.get("EnterUser").d("Enter username"),
    onChange: e => setLogin(e.target.value),
    value: login,
    variant: "outlined",
    InputProps: {
      startAdornment: /*#__PURE__*/_react.default.createElement(_material.InputAdornment, {
        position: "start"
      }, /*#__PURE__*/_react.default.createElement(_Email.default, null)),
      style: {
        paddingLeft: "8px"
      }
    }
  })), /*#__PURE__*/_react.default.createElement(_material.Box, {
    mb: 2
  }, /*#__PURE__*/_react.default.createElement(_material.TextField, {
    fullWidth: true,
    label: _reactIntlUniversal.default.get("Password").d("Password"),
    placeholder: _reactIntlUniversal.default.get("Enterpassword").d("Enter password"),
    type: "password",
    onChange: e => setPassword(e.target.value),
    value: password,
    variant: "outlined",
    InputProps: {
      startAdornment: /*#__PURE__*/_react.default.createElement(_material.InputAdornment, {
        position: "start"
      }, /*#__PURE__*/_react.default.createElement(_Lock.default, null)),
      style: {
        paddingLeft: "8px"
      }
    }
  })), /*#__PURE__*/_react.default.createElement(_material.Box, {
    mb: 2
  }, /*#__PURE__*/_react.default.createElement(_material.TextField, {
    fullWidth: true,
    label: _reactIntlUniversal.default.get("Description").d("Description"),
    placeholder: _reactIntlUniversal.default.get("EnterDescription").d("Enter description"),
    onChange: e => setLogDescription(e.target.value || ""),
    value: logDescription,
    variant: "outlined",
    InputProps: {
      startAdornment: /*#__PURE__*/_react.default.createElement(_material.InputAdornment, {
        position: "start"
      }, /*#__PURE__*/_react.default.createElement(_Keyboard.default, null)),
      style: {
        paddingLeft: "8px"
      }
    }
  })))), /*#__PURE__*/_react.default.createElement(_dmsbasereact.DmsModal.Footer, null, /*#__PURE__*/_react.default.createElement(_dmsbasereact.DmsButton, {
    variant: "contained",
    color: "secondary",
    onClick: handleClose
  }, _reactIntlUniversal.default.get("Close").d("Close")), /*#__PURE__*/_react.default.createElement(_dmsbasereact.DmsButton, {
    variant: "danger",
    color: "primary",
    onClick: handleSubmit
  }, _reactIntlUniversal.default.get("Submit").d("Submit")), fingerPrinterEnabled && /*#__PURE__*/_react.default.createElement(_material.IconButton, {
    onClick: handleSubmitFingerPrinter,
    color: "primary"
  }, /*#__PURE__*/_react.default.createElement(_Fingerprint.default, null)))));
};
exports.DmsDoubleBlind = DmsDoubleBlind;